import { useQuery } from "@tanstack/react-query";
import { ShipTier } from "@voidex/contracts-ts";
import { querySubgraph } from "../web3/subgraph";
import type { ResolvedMission } from "./useMissionHistory";
import { FarmOutcome } from "./usePreTGE";

interface SubgraphResolvedSession {
	id: string;
	shipTokenId: string;
	ship: { tier: "Probe" | "Frigate" | "Dreadnought" };
	outcome: string;
	pointsEarned: string | null;
	shipDestroyed: boolean;
	startedAt: string;
	resolvedAt: string | null;
	resolvedTimestamp: string | null;
	vrfDone: boolean;
}

interface LatestResolvedResult {
	farmSessions: SubgraphResolvedSession[];
}

const tierMap: Record<string, ShipTier> = {
	Probe: ShipTier.Probe,
	Frigate: ShipTier.Frigate,
	Dreadnought: ShipTier.Dreadnought,
};

const outcomeMap: Record<string, FarmOutcome> = {
	Destroyed: FarmOutcome.Destroyed,
	Poor: FarmOutcome.Poor,
	Standard: FarmOutcome.Standard,
	Rich: FarmOutcome.Rich,
	Jackpot: FarmOutcome.Jackpot,
};

/**
 * Polls the subgraph for a newly VRF-resolved farm session.
 * In V2, vrfDone=true means VRF callback has fired (outcome known).
 * For destroyed ships, resolved=true immediately. For survivors, resolved=true only after claim.
 * This hook polls for vrfDone to detect VRF resolution during the mission animation.
 */
export function useMissionOutcome(
	address: `0x${string}` | undefined,
	enabled: boolean,
	knownLatestId: string | null,
) {
	const { data, isLoading } = useQuery({
		queryKey: ["missionOutcome", address, knownLatestId],
		queryFn: async (): Promise<ResolvedMission | null> => {
			if (!address) return null;

			const farmerId = address.toLowerCase();
			const result = await querySubgraph<LatestResolvedResult>(
				`query LatestResolved($farmer: String!) {
					farmSessions(
						where: { farmer: $farmer, vrfDone: true }
						orderBy: resolvedAt
						orderDirection: desc
						first: 1
					) {
						id
						shipTokenId
						ship { tier }
						outcome
						pointsEarned
						shipDestroyed
						startedAt
						resolvedAt
						resolvedTimestamp
						vrfDone
					}
				}`,
				{ farmer: farmerId },
			);

			const latest = result.farmSessions[0];
			if (!latest) return null;

			// If the latest resolved session is the same one we knew about, VRF hasn't resolved yet
			if (knownLatestId !== null && latest.id === knownLatestId) return null;

			return {
				sessionId: BigInt(latest.id),
				shipTokenId: BigInt(latest.shipTokenId),
				shipTier: tierMap[latest.ship.tier] ?? ShipTier.Probe,
				outcome: outcomeMap[latest.outcome] ?? FarmOutcome.Destroyed,
				pointsEarned: BigInt(latest.pointsEarned ?? "0"),
				shipDestroyed: latest.shipDestroyed,
				startedAtBlock: BigInt(latest.startedAt),
				resolvedAtBlock: BigInt(latest.resolvedAt ?? "0"),
				resolvedTimestamp: BigInt(latest.resolvedTimestamp ?? "0"),
			};
		},
		enabled: enabled && !!address,
		refetchInterval: enabled ? 3_000 : false,
		staleTime: 0,
	});

	return {
		mission: data ?? null,
		isPolling: enabled && isLoading,
	};
}

/**
 * Fetches the latest VRF-resolved session ID for snapshot before launch.
 */
export function useLatestResolvedId(address?: `0x${string}`) {
	const { data } = useQuery({
		queryKey: ["latestResolvedId", address],
		queryFn: async (): Promise<string | null> => {
			if (!address) return null;

			const farmerId = address.toLowerCase();
			const result = await querySubgraph<{ farmSessions: { id: string }[] }>(
				`query LatestId($farmer: String!) {
					farmSessions(
						where: { farmer: $farmer, vrfDone: true }
						orderBy: resolvedAt
						orderDirection: desc
						first: 1
					) { id }
				}`,
				{ farmer: farmerId },
			);

			return result.farmSessions[0]?.id ?? null;
		},
		enabled: !!address,
		staleTime: 30_000,
	});

	return data ?? null;
}
